import { productionPlanControllerGetCapacityMatrix } from '@artic/api-client';
import type { CapacityMatrixResponseDto } from '@artic/api-client';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';

import { expectData } from '../../_shared/api';

export const THROUGHPUT_CAPACITY_KEY = 'throughput-capacity' as const;

export type CapacityGranularity = 'week' | 'day';

/**
 * ENG-732 capacity matrix — per-factory, per-week (or per-day) demand/planned
 * cells, computed on read by the backend so a work-order move is visible on
 * the next fetch. Realtime `capacity_updated` events invalidate
 * [THROUGHPUT_CAPACITY_KEY]; the refetchInterval is the fallback for partners
 * without the realtime flag (and the hourly clock recompute).
 */
export function capacityMatrixQueryOptions(args: {
  partnerId: string | undefined;
  from: string;
  to: string;
  granularity: CapacityGranularity;
  /** Poll cadence; the expanded view polls faster than the always-on cards. */
  refetchInterval?: number;
  /**
   * Extra gate on top of `partnerId`, for callers that must compute the range
   * unconditionally but only fetch when their feature flag is on (the summary
   * strip's factory cells). Defaults to on.
   */
  enabled?: boolean;
}) {
  const { partnerId, from, to, granularity, enabled = true } = args;
  return queryOptions({
    queryKey: [THROUGHPUT_CAPACITY_KEY, partnerId, granularity, from, to] as const,
    queryFn: async ({ signal }) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await productionPlanControllerGetCapacityMatrix(
        partnerId,
        { from, to, granularity },
        { signal },
      );
      return expectData<CapacityMatrixResponseDto>(res);
    },
    enabled: enabled && !!partnerId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
    refetchInterval: args.refetchInterval ?? 30_000,
  });
}
